import React from 'react';

import { Box, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core';

import { getComponentDefinition } from 'models';

import { LaunchBadge } from 'components/badges/LaunchBadge';
import { CompanyHeroHeader } from 'components/company-hero-header/CompanyHeroHeader';
import { ComponentDatasheetActionsPublic } from 'components/component-datasheet/components/ComponentDatasheetActions.Public';
import { ComponentDatasheetProductVariants } from 'components/component-datasheet/components/ComponentDatasheetProductVariants';
import { useDatasheetMeta } from 'components/component-datasheet/hooks/use-datasheet-meta';
import { useDatasheetProductSeriesComponents } from 'components/component-datasheet/hooks/use-datasheet-product-series-components';
import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { Metadata } from 'components/page/Metadata';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useIsMobile } from 'hooks/use-is-mobile';

import { useComponentContext } from '../../hooks/use-component-context';
import { Compliances } from './Compliances';
import { Description } from './Description';
import { EMergeAllianceNotice } from './EMergeAllianceNotice';
import { GeneralView } from './General.View';
import { HeaderImages } from './Header.Images';
import { Organizations } from './Organizations';
import { PplReady } from './PplReady';
import { Pricing } from './Pricing';

import cx from './Header.module.scss';

const HeaderHero = ({ initialMode }: { initialMode: DatasheetMode }) => {
    const { component } = useComponentContext();
    const { company } = useCompanyProfile(component.manufacturer);
    const mode = useDatasheetMode();

    let title = initialMode === DatasheetMode.DUPLICATE ? `Duplicate ${component.name}` : component.name;

    if (mode === DatasheetMode.BULK) {
        title = `Editing series: ${component.productSeries}`;
    }

    const { componentType } = useDatasheetMeta(component);
    const { components: variants } = useDatasheetProductSeriesComponents();

    const subtitleItems = [
        componentType?.label,
        !variants || variants.length <= 1 ? component.productIdentifier : <ComponentDatasheetProductVariants />,
        component.productSeries,
    ].filter(Boolean);

    return (
        <Box className={cx.hero}>
            <Metadata title={title} />

            <Stack className={cx.heroContent} gap="xl">
                {company && <CompanyHeroHeader company={company} />}

                <Stack gap={6}>
                    <Title className={cx.title}>{title}</Title>
                    <Text className={cx.subtitle}>
                        {subtitleItems.map((item, index) => (
                            <React.Fragment key={index}>
                                {item}
                                {index < subtitleItems.length - 1 && ' · '}
                            </React.Fragment>
                        ))}

                        <LaunchBadge component={component} ml={6} style={{ verticalAlign: 'middle' }} />
                    </Text>
                </Stack>
            </Stack>
        </Box>
    );
};

const Header = () => {
    const { component } = useComponentContext();
    const { company } = useCompanyProfile(component.manufacturer);
    const isMobile = useIsMobile('sm');

    const description = component.description || component.specificationsSummary;
    const { interoperabilityDataModel } = getComponentDefinition(component.type);

    const facts = (
        <>
            <GeneralView />
            <Compliances />
            <Organizations />
            <PplReady />
        </>
    );

    return (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: 'lg', md: 'xl' }} className={cx.grid}>
            <Stack gap={0} className={cx.images}>
                <HeaderImages
                    images={component.images}
                    manufacturer={company}
                    componentType={component.type}
                    actions={<ComponentDatasheetActionsPublic />}
                />

                {interoperabilityDataModel && <EMergeAllianceNotice />}
            </Stack>

            <Stack gap="lg" className={cx.details}>
                {isMobile ? (
                    <Stack gap={0} className={cx.facts}>
                        {facts}
                    </Stack>
                ) : (
                    <Group gap="xs" align="end">
                        {facts}
                    </Group>
                )}

                {description && (
                    <Box mt={isMobile ? 0 : 'md'}>
                        <Description text={description} />
                    </Box>
                )}

                <Pricing />
            </Stack>
        </SimpleGrid>
    );
};

export { Header, HeaderHero };
